// ::: RSO_CHANGE ::: CAS-358 ::: Secure Device Management

import i18next from "i18next";
import * as Setting from "../Setting";

/**
 * Get user devices of user
 * @param {string} userId - User ID
 * @param {string} page - Page number
 * @param {string} pageSize - Page size
 * @param {string} field - Field to filter
 * @param {string} value - Value to filter
 * @param {string} sortField - Sort field
 * @param {string} sortOrder - Sort order
 * @returns {Promise} Promise object
 */
export function getUserDevicesOfUser(userId, page = "", pageSize = "", field = "", value = "", sortField = "", sortOrder = "") {
  return fetch(`${Setting.ServerUrl}/api/get-user-devices-of-user?userId=${userId}&p=${page}&pageSize=${pageSize}&field=${field}&value=${value}&sortField=${sortField}&sortOrder=${sortOrder}`, {
    method: "GET",
    credentials: "include",
    headers: {
      "Accept-Language": Setting.getAcceptLanguage(),
    },
  }).then(res => res.json());
}

/**
 * Get user device by ID, session ID, or device token
 * @param {string} userId - User ID
 * @param {string} sessionId - Session ID (optional)
 * @param {string} deviceToken - Device Token (optional)
 * @returns {Promise} Promise object
 */
export function getUserDevice(userId, sessionId = "", deviceToken = "") {
  let url = `${Setting.ServerUrl}/api/get-user-device?userId=${userId}`;

  if (sessionId) {
    url += `&sessionId=${sessionId}`;
  } else if (deviceToken) {
    url += `&deviceToken=${deviceToken}`;
  }

  return fetch(url, {
    method: "GET",
    credentials: "include",
    headers: {
      "Accept-Language": Setting.getAcceptLanguage(),
    },
  }).then(res => res.json());
}

/**
 * Add user device
 * @param {Object} userDevice - User device object
 * @returns {Promise} Promise object
 */
export function addUserDevice(userDevice) {
  const newUserDevice = Setting.deepCopy(userDevice);
  return fetch(`${Setting.ServerUrl}/api/add-user-device`, {
    method: "POST",
    credentials: "include",
    body: JSON.stringify(newUserDevice),
    headers: {
      "Accept-Language": Setting.getAcceptLanguage(),
    },
  }).then(res => res.json());
}

/**
 * Check if device is trusted
 * @param {string} identifierType - Type of identifier ('id', 'user_device', or 'session')
 * @param {string} identifiers - Comma-separated identifiers
 * @returns {Promise} Promise object
 */
export function isTrustedUserDevice(identifierType, identifiers) {
  return fetch(`${Setting.ServerUrl}/api/is-trusted-user-device?identifierType=${identifierType}&identifiers=${identifiers}`, {
    method: "GET",
    credentials: "include",
    headers: {
      "Accept-Language": Setting.getAcceptLanguage(),
    },
  }).then(res => res.json());
}

/**
 * Update device's last login time
 * @param {string} identifierType - Type of identifier ('id', 'user_device', or 'session')
 * @param {string} identifiers - Comma-separated identifiers
 * @returns {Promise} Promise object
 */
export function updateLastLoginTime(identifierType, identifiers) {
  return fetch(`${Setting.ServerUrl}/api/update-last-login-time?identifierType=${identifierType}&identifiers=${identifiers}`, {
    method: "PUT",
    credentials: "include",
    headers: {
      "Accept-Language": Setting.getAcceptLanguage(),
    },
  }).then(res => res.json());
}

/**
 * Delete user device
 * @param {string} identifierType - Type of identifier ('id', 'user_device', or 'session')
 * @param {string} identifiers - Comma-separated identifiers
 * @returns {Promise} Promise object
 */
export function deleteUserDevice(identifierType, identifiers) {
  return fetch(`${Setting.ServerUrl}/api/delete-user-device?identifierType=${identifierType}&identifiers=${identifiers}`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Accept-Language": Setting.getAcceptLanguage(),
    },
  }).then(res => res.json());
}

/**
 * Update user device information
 * @param {Object} userDevice - User device object
 * @param {string} fields - Comma-separated fields to update (optional)
 * @returns {Promise} Promise object
 */
export function updateUserDevice(userDevice, fields = "") {
  const newUserDevice = Setting.deepCopy(userDevice);
  let url = `${Setting.ServerUrl}/api/update-user-device`;

  if (fields) {
    url += `?fields=${fields}`;
  }

  return fetch(url, {
    method: "POST",
    credentials: "include",
    body: JSON.stringify(newUserDevice),
    headers: {
      "Accept-Language": Setting.getAcceptLanguage(),
    },
  }).then(res => res.json());
}

/**
 * Mark device as trusted (skip MFA)
 * @param {string} userId - User ID
 * @param {string} deviceToken - Device Token
 * @returns {Promise} Promise object
 */
export function markDeviceAsTrusted(userId, deviceToken) {
  const body = {
    userId: userId,
    deviceToken: deviceToken,
  };

  return fetch(`${Setting.ServerUrl}/api/mark-device-trusted`, {
    method: "POST",
    credentials: "include",
    body: JSON.stringify(body),
    headers: {
      "Accept-Language": Setting.getAcceptLanguage(),
    },
  }).then(res => res.json());
}

/**
 * Get trust status of current device
 * @returns {Promise} Promise object
 */
export function getDeviceTrustStatus() {
  return fetch(`${Setting.ServerUrl}/api/get-device-trust-status`, {
    method: "GET",
    credentials: "include",
    headers: {
      "Accept-Language": Setting.getAcceptLanguage(),
    },
  }).then(res => res.json());
}

/**
 * Mark current device as trusted
 * @returns {Promise} Promise object
 */
export function markCurrentDeviceTrusted() {
  return fetch(`${Setting.ServerUrl}/api/mark-device-trusted`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Accept-Language": Setting.getAcceptLanguage(),
    },
  }).then(res => res.json());
}

export async function isTrustedCurrentDevice() {
  try {
    const res = await fetch(`${Setting.ServerUrl}/api/is-trusted-current-device`, {
      method: "GET",
      credentials: "include",
      headers: {
        "Accept-Language": Setting.getAcceptLanguage(),
      },
    });

    const result = await res.json();

    if (res.ok && result.status === "ok") {
      return {
        status: "ok",
        data: result.data, // true / false
      };
    } else {
      return {
        status: "error",
        data: false,
        msg: result.msg || i18next.t("general:Failed to check device trust status"),
      };
    }
  } catch (err) {
    return {
      status: "error",
      data: false,
      msg: i18next.t("general:connection error"),
    };
  }
}
